// src/screens/CreateBlog.js
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import {
  FileText, Link2, AlignLeft, Type,
  CheckCircle2, AlertCircle, Eye, EyeOff,
  Send, Image, Tag, ArrowLeft, Plus
} from "lucide-react";
import Logo from "../../img/logo.png";
import { useAuth } from "../../contexts/AuthContext";
import "./CreateBlog.css";
import API_BASE from "../../config/api";

function CreateBlog() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState("Content");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    media_url: "",
    is_published: true,
  });

  const navItems = [
    { id: "Content", icon: FileText,  desc: "Title & body"   },
    { id: "Media",   icon: Image,     desc: "Featured image" },
    { id: "Excerpt", icon: AlignLeft, desc: "Short summary"  },
    { id: "Publish", icon: Send,      desc: "Review & post"  },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE}/blog/`,
        {
          title:        formData.title,
          content:      formData.content,
          excerpt:      formData.excerpt,
          media_url:    formData.media_url,
          is_published: formData.is_published,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept:         "application/json",
            Authorization:  `Bearer ${token}`,
          },
        }
      );
      setSuccess(true);
      setTimeout(() => navigate("/blog/list"), 1800);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ── Completion checker ────────────────────────────────────────────────────
  const isDone = (sectionId) => {
    if (sectionId === "Content") return !!(formData.title && formData.content);
    if (sectionId === "Media")   return !!formData.media_url;
    if (sectionId === "Excerpt") return !!formData.excerpt;
    return false;
  };

  // ── Section content ───────────────────────────────────────────────────────
  const renderContent = () => {
    switch (activeSection) {

      case "Content":
        return (
          <div className="cb-animate">
            <div className="cb-field">
              <label className="cb-label">
                <Type size={14} className="cb-label-icon" />
                Post Title <span className="cb-required">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter a compelling blog title..."
                className="cb-input"
                required
              />
            </div>

            <div className="cb-field">
              <label className="cb-label">
                <FileText size={14} className="cb-label-icon" />
                Full Content <span className="cb-required">*</span>
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Write your full blog content here..."
                className="cb-textarea"
                rows={14}
                required
              />
              <span className="cb-char-count">{formData.content.length} characters</span>
            </div>

            {/* Publication status */}
            <div className="cb-field">
              <label className="cb-label">
                <Eye size={14} className="cb-label-icon" />
                Publication Status
              </label>
              <div className="cb-published-row">
                <div
                  className={`cb-toggle ${formData.is_published ? "cb-toggle-on" : ""}`}
                  onClick={() => setFormData((p) => ({ ...p, is_published: !p.is_published }))}
                >
                  <div className="cb-toggle-thumb" />
                </div>
                <div className="cb-published-info">
                  <span className={`cb-published-badge ${formData.is_published ? "published" : "draft"}`}>
                    {formData.is_published
                      ? <><Eye size={12} /> Published</>
                      : <><EyeOff size={12} /> Draft</>}
                  </span>
                  <span className="cb-published-hint">
                    {formData.is_published
                      ? "Post will go live immediately after creation."
                      : "Post will be saved privately as a draft."}
                  </span>
                </div>
              </div>
            </div>

            <div className="cb-next-row">
              <button
                type="button"
                className="cb-btn-next"
                onClick={() => setActiveSection("Media")}
                disabled={!formData.title || !formData.content}
              >
                Next: Media →
              </button>
            </div>
          </div>
        );

      case "Media":
        return (
          <div className="cb-animate">
            <div className="cb-field">
              <label className="cb-label">
                <Link2 size={14} className="cb-label-icon" />
                Media URL
                <span className="cb-label-hint">— featured image or video link</span>
              </label>
              <input
                type="text"
                name="media_url"
                value={formData.media_url}
                onChange={handleInputChange}
                placeholder="https://example.com/image.jpg"
                className="cb-input"
              />
            </div>

            {formData.media_url ? (
              <div className="cb-media-preview-card">
                <p className="cb-media-preview-label">
                  <Eye size={13} /> Live Preview
                </p>
                <img
                  src={formData.media_url}
                  alt="Preview"
                  className="cb-media-preview-img"
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            ) : (
              <div className="cb-media-empty">
                <Image size={40} className="cb-media-empty-icon" />
                <p>Paste a URL above to preview your image</p>
              </div>
            )}

            <div className="cb-next-row cb-next-row-between">
              <button type="button" className="cb-btn-back" onClick={() => setActiveSection("Content")}>
                ← Back
              </button>
              <button type="button" className="cb-btn-next" onClick={() => setActiveSection("Excerpt")}>
                Next: Excerpt →
              </button>
            </div>
          </div>
        );

      case "Excerpt":
        return (
          <div className="cb-animate">
            <div className="cb-field">
              <label className="cb-label">
                <Tag size={14} className="cb-label-icon" />
                Excerpt
                <span className="cb-label-hint">— shown in blog listing cards</span>
              </label>
              <input
                type="text"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                placeholder="A short one-line summary of your post..."
                className="cb-input"
              />
            </div>

            {/* Card preview */}
            <div className="cb-excerpt-preview">
              <p className="cb-excerpt-preview-label">Blog Card Preview</p>
              <div className="cb-excerpt-card">
                {formData.media_url ? (
                  <img
                    src={formData.media_url}
                    alt="thumb"
                    className="cb-excerpt-card-img"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                ) : (
                  <div className="cb-excerpt-card-img-placeholder" />
                )}
                <div className="cb-excerpt-card-body">
                  <span className="cb-excerpt-card-category">BLOG</span>
                  <p className="cb-excerpt-card-title">{formData.title || "Your Post Title"}</p>
                  <p className="cb-excerpt-card-text">
                    {formData.excerpt || "Your excerpt will appear here..."}
                  </p>
                </div>
              </div>
            </div>

            <div className="cb-next-row cb-next-row-between">
              <button type="button" className="cb-btn-back" onClick={() => setActiveSection("Media")}>
                ← Back
              </button>
              <button type="button" className="cb-btn-next" onClick={() => setActiveSection("Publish")}>
                Review & Publish →
              </button>
            </div>
          </div>
        );

      case "Publish":
        return (
          <div className="cb-animate">

            {/* Summary */}
            <div className="cb-summary">
              <p className="cb-summary-heading">Post Summary</p>
              {[
                { key: "Title",     val: formData.title    },
                { key: "Excerpt",   val: formData.excerpt  },
                { key: "Content",   val: formData.content ? `${formData.content.length} characters` : "" },
                { key: "Media URL", val: formData.media_url },
              ].map(({ key, val }) => (
                <div className="cb-summary-row" key={key}>
                  <span className="cb-summary-key">{key}</span>
                  <span className="cb-summary-val">
                    {val || <em className="cb-empty">Not set</em>}
                  </span>
                </div>
              ))}
              <div className="cb-summary-row">
                <span className="cb-summary-key">Status</span>
                <span className={`cb-summary-badge ${formData.is_published ? "published" : "draft"}`}>
                  {formData.is_published
                    ? <><Eye size={11} /> Will be Published</>
                    : <><EyeOff size={11} /> Will be Saved as Draft</>}
                </span>
              </div>
            </div>

            {/* Final publish toggle */}
            <div className="cb-publish-toggle-row">
              <div>
                <h5 className="cb-publish-toggle-title">
                  {formData.is_published
                    ? <><Eye size={16} /> Publish Immediately</>
                    : <><EyeOff size={16} /> Save as Draft</>}
                </h5>
                <p className="cb-publish-toggle-desc">
                  {formData.is_published
                    ? "Post will be live and visible to all readers right after creation."
                    : "Post will be saved privately. You can publish it later."}
                </p>
              </div>
              <div
                className={`cb-toggle cb-toggle-lg ${formData.is_published ? "cb-toggle-on" : ""}`}
                onClick={() => setFormData((p) => ({ ...p, is_published: !p.is_published }))}
              >
                <div className="cb-toggle-thumb" />
              </div>
            </div>

            {/* Action buttons */}
            <div className="cb-actions-bar">
              <button
                type="button"
                className="cb-btn-back"
                onClick={() => setActiveSection("Excerpt")}
                disabled={isLoading}
              >
                ← Back
              </button>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="button"
                  className="cb-btn-secondary"
                  onClick={() => navigate("/blog/list")}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="cb-btn-primary"
                  disabled={isLoading || !formData.title || !formData.content}
                >
                  {isLoading ? (
                    <span className="cb-spinner" />
                  ) : (
                    <>
                      <Plus size={16} />
                      {formData.is_published ? "Publish Post" : "Save Draft"}
                    </>
                  )}
                </button>
              </div>
            </div>

          </div>
        );

      default: return null;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Create Blog Post · Revexia</title>
        <meta name="description" content="Create and publish a new blog post on Revexia." />
      </Helmet>

      <div className="cb-page">

       

        {/* ── Layout ── */}
        <div className="cb-layout">

          {/* ── Sidebar ── */}
          <aside className="cb-sidebar">

            {/* Sidebar header */}
            <div className="cb-sidebar-header">
              <div className="cb-sidebar-icon">
                <Plus size={22} />
              </div>
              <div className="cb-sidebar-header-text">
                <p className="cb-sidebar-title">New Post</p>
                <p className="cb-sidebar-subtitle">
                  {formData.title || "Untitled post"}
                </p>
              </div>
            </div>

            {/* Steps */}
            <nav className="cb-sidebar-nav">
              {navItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`cb-nav-item ${activeSection === item.id ? "active" : ""}`}
                >
                  {/* Step number / check */}
                  <div className={`cb-step-num ${isDone(item.id) ? "done" : ""} ${activeSection === item.id ? "active-step" : ""}`}>
                    {isDone(item.id) ? <CheckCircle2 size={14} /> : index + 1}
                  </div>
                  <div className="cb-nav-item-text">
                    <span className="cb-nav-item-label">{item.id}</span>
                    <span className="cb-nav-item-desc">{item.desc}</span>
                  </div>
                  {activeSection === item.id && <div className="cb-nav-indicator" />}
                </button>
              ))}
            </nav>

            {/* Progress bar */}
            <div className="cb-progress-wrapper">
              <div className="cb-progress-header">
                <span className="cb-progress-label">Progress</span>
                <span className="cb-progress-pct">
                  {Math.round((["Content","Media","Excerpt"].filter(isDone).length / 3) * 100)}%
                </span>
              </div>
              <div className="cb-progress-track">
                <div
                  className="cb-progress-fill"
                  style={{ width: `${Math.round((["Content","Media","Excerpt"].filter(isDone).length / 3) * 100)}%` }}
                />
              </div>
            </div>

            {/* Status badge */}
            <div className={`cb-sidebar-status ${formData.is_published ? "published" : "draft"}`}>
              {formData.is_published
                ? <><Eye size={13} /> Will Publish</>
                : <><EyeOff size={13} /> Will Save as Draft</>}
            </div>

          </aside>

          {/* ── Content Panel ── */}
          <main className="cb-main">
            <form onSubmit={handleSubmit}>
              <div className="cb-panel">
                <div className="cb-panel-mesh" />

                {/* Panel header */}
                <header className="cb-panel-header">
                  <h2 className="cb-panel-title">{activeSection} Settings</h2>
                  <div className="cb-panel-subtitle-row">
                    <div className="cb-panel-dot" />
                    <p className="cb-panel-subtitle">
                      {activeSection === "Content" && "Write your blog title and full content"}
                      {activeSection === "Media"   && "Add a featured image URL for your post"}
                      {activeSection === "Excerpt" && "Write a short summary for the blog listing"}
                      {activeSection === "Publish" && "Review all fields and publish or save as draft"}
                    </p>
                  </div>
                </header>

                {/* Alerts */}
                {error && (
                  <div className="cb-alert cb-alert-error">
                    <AlertCircle size={16} /> <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="cb-alert cb-alert-success">
                    <CheckCircle2 size={16} />
                    <span>
                      Post {formData.is_published ? "published" : "saved as draft"} successfully! Redirecting...
                    </span>
                  </div>
                )}

                {renderContent()}
              </div>
            </form>
          </main>

        </div>
      </div>
    </>
  );
}

export default CreateBlog;
